import { sha256 } from '@noble/hashes/sha2';
import { bytesToHex } from '@noble/hashes/utils';

const MIB = 1024 * 1024;
const READ_CHUNK = 8 * MIB;

async function bytesOf(blob: Blob): Promise<Uint8Array> {
  return new Uint8Array(await blob.arrayBuffer());
}

/** Reads the blob in pieces, so a 10 GB file never sits in memory. */
export async function sha256OfBlob(blob: Blob, onProgress?: (hashedBytes: number) => void): Promise<string> {
  const hash = sha256.create();
  for (let at = 0; at < blob.size; at += READ_CHUNK) {
    const end = Math.min(at + READ_CHUNK, blob.size);
    hash.update(await bytesOf(blob.slice(at, end)));
    onProgress?.(end);
  }
  return bytesToHex(hash.digest());
}

/** Same bytes as `sampledHex` in supabase/functions/_shared/files/objects.ts. */
export async function sampledSha256(blob: Blob): Promise<string> {
  const size = blob.size;
  const span = Math.min(MIB, size);
  const parts = size <= span * 2 ? [blob] : [blob.slice(0, span), blob.slice(size - span)];

  const hash = sha256.create();
  hash.update(new TextEncoder().encode(`${size}:`));
  for (const part of parts) hash.update(await bytesOf(part));
  return bytesToHex(hash.digest());
}

type WorkerReply = { type: 'progress'; hashedBytes: number } | { type: 'done'; hex: string } | { type: 'error' };

/** Hashes off the main thread when a worker is available. */
export function hashFile(file: Blob, onProgress: (hashedBytes: number) => void, signal: AbortSignal): Promise<string> {
  if (typeof Worker === 'undefined') return sha256OfBlob(file, onProgress);

  return new Promise((resolve, reject) => {
    const worker = new Worker(new URL('./documents-hash.worker.ts', import.meta.url), { type: 'module' });
    const stop = () => {
      worker.terminate();
      reject(new DOMException('Upload cancelled', 'AbortError'));
    };
    signal.addEventListener('abort', stop, { once: true });

    worker.onmessage = (event: MessageEvent<WorkerReply>) => {
      const reply = event.data;
      if (reply.type === 'progress') {
        onProgress(reply.hashedBytes);
        return;
      }
      signal.removeEventListener('abort', stop);
      worker.terminate();
      if (reply.type === 'done') resolve(reply.hex);
      else reject(new Error('Could not read the file'));
    };
    worker.onerror = () => {
      signal.removeEventListener('abort', stop);
      worker.terminate();
      reject(new Error('Could not read the file'));
    };
    worker.postMessage(file);
  });
}
